import { ApolloCache } from '@apollo/client';
import { User } from '@hiberworld/chat-client';
import {
  BlockedUsersDocument,
  BlockedUsersQuery,
  BlockedUsersQueryVariables,
  useBlockUserMutation,
  UserByUsernameDocument,
  UserByUsernameQuery,
  UserByUsernameQueryVariables,
  useUnblockUserMutation,
} from 'generated/graphql';
import { useMe } from './useMe';

const readBlockedUsersFromCache = (store: ApolloCache<UserByUsernameQuery>, username: string) => {
  return store.readQuery<UserByUsernameQuery, UserByUsernameQueryVariables>({
    query: UserByUsernameDocument,
    variables: { username },
  });
};

const writeBlockedUsersToCache = (
  store: ApolloCache<UserByUsernameQuery>,
  username: string,
  data: UserByUsernameQuery
) => {
  store.writeQuery<UserByUsernameQuery, UserByUsernameQueryVariables>({
    query: UserByUsernameDocument,
    data,
    variables: { username },
  });
};

export const useBlock = () => {
  const { me } = useMe();

  const [blockUserMutation] = useBlockUserMutation();
  const [unblockUserMutation] = useUnblockUserMutation();

  const blockUser = (user: Pick<User, 'username' | 'id'>) => {
    blockUserMutation({
      notifyOnNetworkStatusChange: true,
      variables: { userId: user.id },
      optimisticResponse: {
        __typename: 'Mutation',
        blockUser: {
          __typename: 'BlockedUser',
          id: '-1',
          user1Id: me?.id ?? '',
          user2Id: user.id,
          blockedAt: new Date(),
        },
      },
      update: store => {
        let userData: UserByUsernameQuery | null;
        try {
          userData = readBlockedUsersFromCache(store, user.username);

          if (userData?.userByUsername) {
            userData.userByUsername.haveIBlocked = true;
          }

          if (userData) {
            writeBlockedUsersToCache(store, user.username, userData);
          }
          const blockedUsersData = store.readQuery<BlockedUsersQuery, BlockedUsersQueryVariables>({
            query: BlockedUsersDocument,
          });

          if (blockedUsersData?.me?.blockedUsers && userData?.userByUsername) {
            blockedUsersData?.me.blockedUsers.push({
              __typename: 'BlockedUser',
              user2: {
                __typename: 'User',
                id: userData.userByUsername.id,
                username: userData.userByUsername.username,
                pictureUrl: userData.userByUsername.pictureUrl,
                level: userData.userByUsername.level,
                haveIBlocked: true,
              },
            });
          }

          if (blockedUsersData) {
            store.writeQuery<BlockedUsersQuery, BlockedUsersQueryVariables>({
              query: BlockedUsersDocument,
              data: blockedUsersData,
            });
          }
        } catch (e) {
          console.error(e);
        }
      },
    });
  };

  const unblockUser = (user: Pick<User, 'username' | 'id'>) => {
    unblockUserMutation({
      notifyOnNetworkStatusChange: true,
      variables: { userId: user.id },
      optimisticResponse: { __typename: 'Mutation', unblockUser: true },
      update: store => {
        try {
          const userData = readBlockedUsersFromCache(store, user.username);

          if (userData?.userByUsername) {
            userData.userByUsername.haveIBlocked = false;
          }

          if (userData) {
            writeBlockedUsersToCache(store, user.username, userData);
          }
        } catch (e) {
          console.error(e);
        }
        try {
          const data = store.readQuery<BlockedUsersQuery, BlockedUsersQueryVariables>({ query: BlockedUsersDocument });

          if (data?.me?.blockedUsers) {
            data.me.blockedUsers.forEach((blockedUser, i) => {
              if (blockedUser.user2.id === user.id) {
                data.me?.blockedUsers?.splice(i, 1);
              }
            });
          }

          if (data) {
            store.writeQuery<BlockedUsersQuery, BlockedUsersQueryVariables>({ query: BlockedUsersDocument, data });
          }
        } catch (e) {
          console.error(e);
        }
      },
    });
  };

  return { blockUser, unblockUser };
};
